import React, { useState, useCallback, useEffect, useRef } from 'react';
import { apiService } from '../services/apiService';
import {
  TrendingUp, RefreshCw, Trophy, X, Mail, MapPin, Phone, Send, MessageSquare,
  Sparkles, Clock, ChevronDown, ChevronUp, Inbox, CalendarClock, Flame, Reply as ReplyIcon,
  Search, LayoutGrid, Table as TableIcon, Star, ArrowUpDown,
} from 'lucide-react';

const C = {
  bg: 'var(--c-bg)', bg1: 'var(--c-bg-1)', bg2: 'var(--c-bg-2)', surface: 'var(--c-surface)',
  border: 'var(--c-border)', primary: 'var(--c-primary)', text: 'var(--c-text)',
  text2: 'var(--c-text-2)', text3: 'var(--c-text-3)',
};

const STAGES = [
  { key: 'new', label: 'New', color: '#3B82F6' },
  { key: 'contacted', label: 'Contacted', color: '#8B5CF6' },
  { key: 'interested', label: 'Interested', color: '#F59E0B' },
  { key: 'qualified', label: 'Qualified', color: '#10B981' },
  { key: 'proposal_sent', label: 'Proposal sent', color: '#0EA5E9' },
  { key: 'negotiating', label: 'Negotiating', color: '#6366F1' },
  { key: 'won', label: 'Won', color: '#00B386' },
  { key: 'lost', label: 'Lost', color: '#EF4444' },
];
const stageMeta = (k) => STAGES.find(s => s.key === k) || { key: k, label: k, color: '#6B7280' };

const INTENT_META = {
  hot_lead: { label: 'Hot lead', color: '#EF4444', bg: 'rgba(239,68,68,0.14)' },
  meeting_request: { label: 'Meeting', color: '#10B981', bg: 'rgba(16,185,129,0.14)' },
  needs_info: { label: 'Wants info', color: '#F59E0B', bg: 'rgba(245,158,11,0.14)' },
  follow_up_later: { label: 'Follow up later', color: '#3B82F6', bg: 'rgba(59,130,246,0.14)' },
  out_of_office: { label: 'Out of office', color: '#8B5CF6', bg: 'rgba(139,92,246,0.14)' },
  not_interested: { label: 'Not interested', color: '#6B7280', bg: 'rgba(107,114,128,0.14)' },
  other: { label: 'Replied', color: '#6B7280', bg: 'rgba(107,114,128,0.14)' },
};
const SENT_EMOJI = { positive: '😊', neutral: '😐', negative: '🙁' };
const REP_COLORS = ['#00B386', '#3B82F6', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
const repColor = (name) => { let h = 0; for (const ch of (name || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0; return REP_COLORS[h % REP_COLORS.length]; };
const initials = (n) => (n || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';

const ago = (iso) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};
const fmtDate = (d) => { try { const t = new Date(d); return t.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) + ' · ' + t.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const daysSince = (iso) => iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : 0;

// follow-up: next touch + whether overdue
const FU_DUE = [3, 7, 14];
const fuStatus = (l) => {
  const fc = l.follow_up_count || 0;
  if (fc >= 3) return { label: 'complete', due: false, next: null };
  const dueDay = FU_DUE[fc];
  const since = daysSince(l.emailed_at);
  const inDays = dueDay - since;
  return { next: fc + 1, due: inDays <= 0, label: inDays <= 0 ? 'due now' : `in ${inDays}d` };
};

const edgeFor = (l) => {
  if (l.unread && (l.score || 0) >= 60) return '#EF4444';
  if (l.stage === 'won') return '#00B386';
  if (l.stage === 'interested') return '#F59E0B';
  if (l.stage === 'qualified') return '#10B981';
  if (l.stage === 'proposal_sent') return '#0EA5E9';
  if (l.stage === 'negotiating') return '#6366F1';
  if (l.stage === 'lost') return '#6B7280';
  if (l.unread) return '#3B82F6';
  return C.border;
};

const SEGMENTS = [
  { key: 'all', label: 'All' },
  { key: 'hot', label: 'Hot', icon: Flame },
  { key: 'replies', label: 'Replies', icon: ReplyIcon },
  { key: 'followup', label: 'Follow-up', icon: CalendarClock },
  { key: 'interested', label: 'Interested', icon: Star },
  { key: 'won', label: 'Won', icon: Trophy },
];
const SEG_TEST = {
  hot: l => (l.score || 0) >= 60 || l.stage === 'interested',
  replies: l => l.has_replied,
  interested: l => l.stage === 'interested',
  won: l => l.stage === 'won',
};

const LeadPipeline = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [segment, setSegment] = useState('all');
  const [view, setView] = useState('table');
  const [sort, setSort] = useState({ key: 'score', dir: 'desc' });
  const [repFilter, setRepFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [coldOpen, setColdOpen] = useState(false);
  const [coldLeads, setColdLeads] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isLoadingLead, setIsLoadingLead] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [stageMenuOpen, setStageMenuOpen] = useState(false);
  const [wonModal, setWonModal] = useState(null);
  const [dealAmount, setDealAmount] = useState('');
  const [isSavingDeal, setIsSavingDeal] = useState(false);
  const [toasts, setToasts] = useState([]);
  const threadEndRef = useRef(null);

  const showToast = useCallback((type, msg) => {
    const id = Date.now(); setToasts(p => [...p, { id, type, msg }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 5000);
  }, []);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try { const r = await apiService.getCockpit(); setData(r); }
    catch { showToast('error', 'Failed to load pipeline'); }
    finally { setIsLoading(false); }
  }, [showToast]);
  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { const t = setInterval(fetchData, 90000); return () => clearInterval(t); }, [fetchData]);

  const selectSegment = (seg) => {
    setSegment(seg);
    setSort(seg === 'followup' ? { key: 'overdue', dir: 'desc' } : { key: 'score', dir: 'desc' });
  };
  const toggleSort = (key) => setSort(s => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const viewLead = useCallback(async (id) => {
    setIsLoadingLead(true); setStageMenuOpen(false); setReplyText(''); setSelectedLead({ _loading: true });
    try { const d = await apiService.getLeadDetails(id); setSelectedLead(d.data); }
    catch { showToast('error', 'Failed to load lead'); setSelectedLead(null); }
    finally { setIsLoadingLead(false); }
  }, [showToast]);

  const moveToStage = useCallback(async (leadId, stage, name) => {
    if (stage === 'won') { setDealAmount(''); setWonModal({ leadId, name: name || 'Lead' }); return; }
    try { await apiService.updateLeadStage(leadId, stage); showToast('success', `Moved to ${stage}`); fetchData(); viewLead(leadId); }
    catch { showToast('error', 'Failed'); }
  }, [fetchData, showToast, viewLead]);

  const confirmWon = useCallback(async () => {
    const amt = parseFloat(dealAmount);
    if (!amt || amt <= 0) { showToast('error', 'Enter a valid amount'); return; }
    setIsSavingDeal(true);
    try {
      await apiService.updateLeadStage(wonModal.leadId, 'won');
      await apiService.updateLeadValue(wonModal.leadId, amt);
      showToast('success', `🏆 R${amt.toLocaleString()} recorded!`);
      setWonModal(null); setDealAmount(''); setSelectedLead(null); fetchData();
    } catch { showToast('error', 'Failed to save'); }
    finally { setIsSavingDeal(false); }
  }, [wonModal, dealAmount, fetchData, showToast]);

  const openCold = useCallback(async () => {
    setColdOpen(o => !o);
    if (!coldLeads) { try { const r = await apiService.getLeads('contacted', 50); setColdLeads(r.leads || []); } catch { setColdLeads([]); } }
  }, [coldLeads]);

  // ── conversation thread (reused) ──
  const conversations = selectedLead?.conversations || [];
  const allMsgs = conversations.flatMap(c => c.replies || []);
  const inbound = allMsgs.filter(m => (m.direction || 'inbound') !== 'outbound' && !m.is_noise);
  const lastInbound = inbound.length ? inbound[inbound.length - 1] : null;
  const hasOutRows = allMsgs.some(m => m.direction === 'outbound');
  let suggested = lastInbound?.ai_suggested_responses;
  if (typeof suggested === 'string') { try { suggested = JSON.parse(suggested); } catch { suggested = null; } }
  suggested = Array.isArray(suggested) ? suggested.slice(0, 3) : [];
  const thread = [];
  if (selectedLead?.profile?.email_sent) thread.push({ dir: 'out', kind: 'campaign', subject: selectedLead.profile.email_subject, at: selectedLead.profile.email_sent_at });
  conversations.forEach(c => (c.replies || []).forEach(r => {
    if (r.direction === 'outbound') thread.push({ dir: 'out', kind: 'response', body: r.body, at: r.received_at, account: r.from_email });
    else { thread.push({ dir: 'in', kind: 'reply', reply: r, at: r.received_at }); if (r.reply_sent && !hasOutRows) thread.push({ dir: 'out', kind: 'response', body: r.reply_sent, at: r.replied_at }); }
  }));
  thread.sort((a, b) => new Date(a.at || 0) - new Date(b.at || 0));
  useEffect(() => { if (selectedLead && threadEndRef.current) threadEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [selectedLead]);

  const sendReply = useCallback(async () => {
    if (!replyText.trim() || !lastInbound) return;
    setIsSendingReply(true);
    try {
      const res = await apiService.sendResponseToReply(lastInbound.id, replyText.trim());
      if (res.status === 'success') { showToast('success', '✉️ Response sent'); setReplyText(''); viewLead(selectedLead.lead.id); }
      else showToast('error', res.error || 'Failed to send');
    } catch { showToast('error', 'Failed to send'); }
    finally { setIsSendingReply(false); }
  }, [replyText, lastInbound, selectedLead, showToast, viewLead]);

  // ── segment + sort + filter ──
  const ENG = data?.engaged || [];
  const FUS = data?.followups || [];
  const counts = {
    all: ENG.length, hot: ENG.filter(SEG_TEST.hot).length, replies: ENG.filter(SEG_TEST.replies).length,
    followup: FUS.length, interested: ENG.filter(SEG_TEST.interested).length, won: ENG.filter(SEG_TEST.won).length,
  };
  const isFU = segment === 'followup';
  let rows = isFU ? [...FUS] : (SEG_TEST[segment] ? ENG.filter(SEG_TEST[segment]) : [...ENG]);
  if (repFilter) rows = rows.filter(l => l.rep === repFilter);
  if (search) { const q = search.toLowerCase(); rows = rows.filter(l => (l.company || '').toLowerCase().includes(q) || (l.email || '').toLowerCase().includes(q) || (l.rep || '').toLowerCase().includes(q)); }
  const VAL = {
    score: l => l.score || 0, activity: l => new Date(l.last_reply_at || 0).getTime(),
    company: l => (l.company || '').toLowerCase(), rep: l => (l.rep || '').toLowerCase(),
    stage: l => l.stage || '', intent: l => l.intent || '',
    touches: l => l.follow_up_count || 0, overdue: l => -new Date(l.emailed_at || 0).getTime(),
  };
  const sortKey = VAL[sort.key] ? sort.key : (isFU ? 'overdue' : 'score');
  rows.sort((a, b) => { const va = VAL[sortKey](a), vb = VAL[sortKey](b); const c = va < vb ? -1 : va > vb ? 1 : 0; return sort.dir === 'asc' ? c : -c; });
  const reps = (data?.reps && data.reps.length)
    ? data.reps
    : Array.from(new Set([...ENG, ...FUS].map(l => l.rep).filter(Boolean)));

  const tdy = data?.today || {}; const stats = data?.stats || {}; const cold = data?.cold || {};
  const trend = cold.trend || []; const maxTrend = Math.max(1, ...trend.map(d => d.count));

  const s = {
    page: { minHeight: 'calc(100vh - 64px)', background: C.bg, padding: '26px 24px' },
    inner: { maxWidth: 1320, margin: '0 auto' },
    todayGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14, marginBottom: 22 },
    tile: (accent, live) => ({ position: 'relative', background: C.surface, border: `1px solid ${live ? accent : C.border}`, borderRadius: 16, padding: '16px 18px', overflow: 'hidden' }),
    tab: (active, accent) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 10, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', border: `1px solid ${active ? (accent || C.primary) : C.border}`, background: active ? `${accent || 'var(--c-primary)'}1f` : C.surface, color: active ? (accent || C.primary) : C.text2, fontFamily: 'Inter,sans-serif' }),
    chip: (active) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${active ? C.primary : C.border}`, background: active ? 'rgba(0,179,134,0.10)' : C.surface, color: active ? C.primary : C.text2, fontFamily: 'Inter,sans-serif' }),
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 },
    card: (edge) => ({ background: C.bg1, border: `1px solid ${C.border}`, borderLeft: `3px solid ${edge}`, borderRadius: 12, padding: '13px 15px', cursor: 'pointer', transition: 'transform .13s, box-shadow .13s, border-color .13s' }),
    badge: (color, bg) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 99, fontSize: 10.5, fontWeight: 700, color, background: bg, whiteSpace: 'nowrap' }),
    btn2: { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 13px', background: C.surface, color: C.text, fontWeight: 600, fontSize: 12.5, border: `1px solid ${C.border}`, borderRadius: 10, cursor: 'pointer', fontFamily: 'Inter,sans-serif' },
    input: { width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: '8px 12px 8px 32px', fontSize: 13, fontFamily: 'Inter,sans-serif', outline: 'none', boxSizing: 'border-box' },
    tableWrap: { overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: 14, background: C.bg1 },
    th: (sortable) => ({ textAlign: 'left', padding: '11px 14px', fontSize: 11, fontWeight: 700, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap', cursor: sortable ? 'pointer' : 'default', userSelect: 'none', borderBottom: `1px solid ${C.border}`, background: C.bg2, position: 'sticky', top: 0 }),
    td: { padding: '11px 14px', fontSize: 13, color: C.text2, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(5,11,20,0.72)', backdropFilter: 'blur(6px)', zIndex: 100 },
    drawer: { position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(760px,100vw)', zIndex: 110, background: C.bg1, borderLeft: '1px solid var(--c-border-bright)', display: 'flex', flexDirection: 'column', boxShadow: '-24px 0 60px rgba(0,0,0,0.45)', animation: 'slideIn .22s ease-out' },
    bIn: { alignSelf: 'flex-start', maxWidth: '80%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '14px 14px 14px 4px', padding: '11px 14px' },
    bOut: { alignSelf: 'flex-end', maxWidth: '80%', background: 'rgba(0,179,134,0.10)', border: '1px solid rgba(0,179,134,0.28)', borderRadius: '14px 14px 4px 14px', padding: '11px 14px' },
    btnP: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '11px 22px', background: 'linear-gradient(135deg, var(--c-primary), var(--c-primary-dim))', color: 'var(--c-btn-text)', fontWeight: 700, fontSize: 13.5, border: 'none', borderRadius: 10, cursor: 'pointer', fontFamily: 'Inter,sans-serif' },
    textarea: { width: '100%', minHeight: 80, resize: 'vertical', background: C.surface, border: '1px solid var(--c-border-bright)', borderRadius: 12, color: C.text, padding: '11px 13px', fontSize: 13.5, lineHeight: 1.6, fontFamily: 'Inter,sans-serif', outline: 'none', boxSizing: 'border-box' },
  };

  const TILES = [
    { key: 'new_replies', label: 'New replies', hint: 'unread, need you', icon: ReplyIcon, accent: '#EF4444', seg: 'replies' },
    { key: 'replies_today', label: 'Replies (24h)', hint: 'came in today', icon: Inbox, accent: '#3B82F6', seg: 'replies' },
    { key: 'followups_due', label: 'Follow-ups due', hint: 'auto-send 09:30', icon: CalendarClock, accent: '#F59E0B', seg: 'followup' },
    { key: 'hot_leads', label: 'Hot / interested', hint: 'ready to talk', icon: Flame, accent: '#00B386', seg: 'hot' },
  ];

  const SortArrow = ({ k }) => sort.key === k ? (sort.dir === 'desc' ? <ChevronDown size={12} style={{ verticalAlign: 'middle' }} /> : <ChevronUp size={12} style={{ verticalAlign: 'middle' }} />) : <ArrowUpDown size={11} style={{ verticalAlign: 'middle', opacity: 0.4 }} />;

  const lead = selectedLead?.lead; const profile = selectedLead?.profile; const cur = stageMeta(lead?.stage);

  return (
    <div style={s.page}>
      <style>{`
        @keyframes slideIn { from { transform: translateX(40px); opacity:0 } to { transform: translateX(0); opacity:1 } }
        @keyframes spin { to { transform: rotate(360deg) } }
        .dc-card:hover { transform: translateY(-2px); box-shadow: 0 8px 22px rgba(0,0,0,0.22); border-color: var(--c-border-bright) !important }
        .dc-row:hover { background: var(--c-surface) }
        .dc-sug:hover { border-color: var(--c-primary) !important; color: var(--c-primary) !important }
      `}</style>
      <div style={s.inner}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <TrendingUp size={22} color={C.primary} />
              <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 24, fontWeight: 700, color: C.text, margin: 0 }}>Deal Cockpit</h1>
            </div>
            <p style={{ color: C.text2, fontSize: 13, margin: 0 }}>{stats.engaged_total || 0} engaged · {stats.reply_rate || 0}% reply rate · {stats.won || 0} won · R{(stats.total_value || 0).toLocaleString()} tracked</p>
          </div>
          <button onClick={fetchData} style={s.btn2}><RefreshCw size={14} style={{ animation: isLoading ? 'spin 1s linear infinite' : 'none' }} /> Refresh</button>
        </div>

        {/* TODAY bar (tiles jump to their segment) */}
        <div style={s.todayGrid}>
          {TILES.map(({ key, label, hint, icon: Icon, accent, seg }) => {
            const val = tdy[key] || 0; const live = val > 0 && (key === 'new_replies' || key === 'hot_leads');
            return (
              <div key={key} onClick={() => selectSegment(seg)} style={{ ...s.tile(accent, live), cursor: 'pointer' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${accent}, transparent)` }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 30, fontWeight: 700, color: val ? accent : C.text3, lineHeight: 1 }}>{val}</div>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text, marginTop: 7 }}>{label}</div>
                    <div style={{ fontSize: 11, color: C.text3 }}>{hint}</div>
                  </div>
                  <div style={{ width: 40, height: 40, borderRadius: 11, background: `${accent}1f`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={19} color={accent} /></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Segment tabs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {SEGMENTS.map(seg => {
            const Icon = seg.icon; const accent = seg.key === 'hot' ? '#EF4444' : seg.key === 'won' ? '#00B386' : seg.key === 'interested' ? '#F59E0B' : seg.key === 'followup' ? '#F59E0B' : C.primary;
            return (
              <button key={seg.key} onClick={() => selectSegment(seg.key)} style={s.tab(segment === seg.key, accent)}>
                {Icon && <Icon size={13} />}{seg.label}
                <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.85 }}>{counts[seg.key] ?? 0}</span>
              </button>
            );
          })}
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <div style={{ display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
            <button onClick={() => setView('table')} style={{ ...s.btn2, border: 'none', borderRadius: 0, background: view === 'table' ? 'rgba(0,179,134,0.12)' : C.surface, color: view === 'table' ? C.primary : C.text2 }}><TableIcon size={14} /> Table</button>
            <button onClick={() => setView('cards')} style={{ ...s.btn2, border: 'none', borderRadius: 0, borderLeft: `1px solid ${C.border}`, background: view === 'cards' ? 'rgba(0,179,134,0.12)' : C.surface, color: view === 'cards' ? C.primary : C.text2 }}><LayoutGrid size={14} /> Cards</button>
          </div>
          {view === 'cards' && (
            <select value={sort.key} onChange={e => setSort({ key: e.target.value, dir: e.target.value === 'company' ? 'asc' : 'desc' })} style={{ ...s.btn2, paddingRight: 26 }}>
              {isFU ? <><option value="overdue">Most overdue</option><option value="touches">Touches</option><option value="company">Company</option></>
                : <><option value="score">Score</option><option value="activity">Last activity</option><option value="company">Company</option></>}
            </select>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span onClick={() => setRepFilter(null)} style={s.chip(!repFilter)}>All reps</span>
            {reps.map(r => <span key={r} onClick={() => setRepFilter(r === repFilter ? null : r)} style={s.chip(repFilter === r)}><span style={{ width: 7, height: 7, borderRadius: 99, background: repColor(r) }} />{r}</span>)}
          </div>
          <div style={{ position: 'relative', marginLeft: 'auto', minWidth: 200 }}>
            <Search size={14} color={C.text3} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search company, rep…" style={s.input} />
          </div>
        </div>

        {/* LIST */}
        {isLoading && !data ? (
          <div style={{ textAlign: 'center', color: C.text3, fontSize: 13, padding: 50 }}>Loading…</div>
        ) : !rows.length ? (
          <div style={{ textAlign: 'center', padding: '46px 24px', background: C.surface, border: `1px dashed ${C.border}`, borderRadius: 16, color: C.text2 }}>
            <MessageSquare size={32} color={C.text3} style={{ marginBottom: 8 }} />
            <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 3px' }}>Nothing here yet</p>
            <p style={{ fontSize: 12.5, color: C.text3, margin: 0 }}>{isFU ? 'Follow-ups begin the day after a lead is emailed.' : 'Replies and interested leads land here as they arrive.'}</p>
          </div>
        ) : view === 'table' ? (
          <div style={s.tableWrap}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
              <thead>
                {isFU ? (
                  <tr>
                    <th style={s.th(true)} onClick={() => toggleSort('company')}>Company <SortArrow k="company" /></th>
                    <th style={s.th(true)} onClick={() => toggleSort('rep')}>Rep <SortArrow k="rep" /></th>
                    <th style={s.th(true)} onClick={() => toggleSort('touches')}>Follow-ups <SortArrow k="touches" /></th>
                    <th style={s.th(true)} onClick={() => toggleSort('overdue')}>Next nudge <SortArrow k="overdue" /></th>
                    <th style={s.th(false)}>Emailed</th>
                  </tr>
                ) : (
                  <tr>
                    <th style={s.th(true)} onClick={() => toggleSort('company')}>Company <SortArrow k="company" /></th>
                    <th style={s.th(true)} onClick={() => toggleSort('rep')}>Rep <SortArrow k="rep" /></th>
                    <th style={s.th(true)} onClick={() => toggleSort('stage')}>Stage <SortArrow k="stage" /></th>
                    <th style={s.th(true)} onClick={() => toggleSort('intent')}>Intent <SortArrow k="intent" /></th>
                    <th style={s.th(true)} onClick={() => toggleSort('score')}>Score <SortArrow k="score" /></th>
                    <th style={s.th(true)} onClick={() => toggleSort('activity')}>Last reply <SortArrow k="activity" /></th>
                  </tr>
                )}
              </thead>
              <tbody>
                {rows.map(l => {
                  const im = INTENT_META[l.intent]; const st = stageMeta(l.stage); const fs = isFU ? fuStatus(l) : null;
                  return (
                    <tr key={l.lead_id} className="dc-row" onClick={() => viewLead(l.lead_id)} style={{ cursor: 'pointer' }}>
                      <td style={{ ...s.td, color: C.text, fontWeight: 600, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                          {l.unread && <span style={{ width: 7, height: 7, borderRadius: 99, background: '#EF4444' }} />}
                          {l.company}
                        </span>
                        <div style={{ fontSize: 11, color: C.text3, fontWeight: 400 }}>{l.email}</div>
                      </td>
                      <td style={s.td}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 18, height: 18, borderRadius: 5, background: repColor(l.rep), color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{initials(l.rep)}</span>{l.rep}</span></td>
                      {isFU ? (
                        <>
                          <td style={s.td}>{[0, 1, 2].map(i => <span key={i} style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 99, marginRight: 3, background: i < (l.follow_up_count || 0) ? C.primary : C.border }} />)}</td>
                          <td style={s.td}><span style={s.badge(fs.due ? '#EF4444' : '#F59E0B', fs.due ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)')}>FU{fs.next} · {fs.label}</span></td>
                          <td style={{ ...s.td, color: C.text3 }}>{daysSince(l.emailed_at)}d ago</td>
                        </>
                      ) : (
                        <>
                          <td style={s.td}><span style={s.badge(st.color, `${st.color}1f`)}>{st.label}</span></td>
                          <td style={s.td}>{im ? <span style={s.badge(im.color, im.bg)}>{im.label}</span> : <span style={{ color: C.text3 }}>—</span>}</td>
                          <td style={{ ...s.td, fontWeight: 700, color: (l.score || 0) >= 60 ? '#EF4444' : C.text }}>{(l.score || 0) >= 60 ? '🔥 ' : ''}{l.score || 0}</td>
                          <td style={{ ...s.td, color: C.text3 }}>{l.last_reply_at ? ago(l.last_reply_at) : '—'} {l.sentiment ? SENT_EMOJI[l.sentiment] : ''}</td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={s.grid}>
            {rows.map(l => {
              const im = INTENT_META[l.intent]; const fs = isFU ? fuStatus(l) : null;
              return (
                <div key={l.lead_id} className="dc-card" style={s.card(isFU ? (fs.due ? '#EF4444' : '#F59E0B') : edgeFor(l))} onClick={() => viewLead(l.lead_id)}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                        {l.unread && <span style={{ width: 8, height: 8, borderRadius: 99, background: '#EF4444', flexShrink: 0 }} />}
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.company}</span>
                      </div>
                      <div style={{ fontSize: 11.5, color: C.text3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.email}</div>
                    </div>
                    {isFU ? <span style={s.badge(fs.due ? '#EF4444' : '#F59E0B', fs.due ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)')}>FU{fs.next} · {fs.label}</span>
                      : <span style={s.badge(stageMeta(l.stage).color, `${stageMeta(l.stage).color}1f`)}>{stageMeta(l.stage).label}</span>}
                  </div>
                  {!isFU && l.reply_snippet && <div style={{ fontSize: 12, color: C.text2, margin: '9px 0', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>“{l.reply_snippet.trim()}”</div>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    {!isFU && im && <span style={s.badge(im.color, im.bg)}>{im.label}</span>}
                    {!isFU && (l.score || 0) >= 60 && <span style={s.badge('#EF4444', 'rgba(239,68,68,0.12)')}>🔥 {l.score}</span>}
                    {isFU && [0, 1, 2].map(i => <span key={i} style={{ width: 8, height: 8, borderRadius: 99, background: i < (l.follow_up_count || 0) ? C.primary : C.border }} />)}
                    <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <span title={l.rep} style={{ width: 20, height: 20, borderRadius: 6, background: repColor(l.rep), color: '#fff', fontSize: 9.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{initials(l.rep)}</span>
                      <span style={{ fontSize: 11, color: C.text3, display: 'inline-flex', alignItems: 'center', gap: 3 }}><Clock size={10} />{isFU ? `${daysSince(l.emailed_at)}d` : ago(l.last_reply_at)}</span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Cold tile */}
        <div style={{ marginTop: 20, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>❄️</span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{(cold.count || 0).toLocaleString()} contacted, no reply yet</div>
                <div style={{ fontSize: 11.5, color: C.text3 }}>Nurtured automatically via follow-ups. Check the Follow-up tab.</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 34, marginLeft: 'auto' }}>
              {trend.map((d, i) => <div key={i} title={`${d.date}: ${d.count}`} style={{ width: 9, height: `${Math.max(8, (d.count / maxTrend) * 34)}px`, background: C.primary, opacity: 0.35 + 0.65 * (d.count / maxTrend), borderRadius: 2 }} />)}
            </div>
            <button onClick={openCold} style={s.btn2}>{coldOpen ? 'Hide' : 'Peek'}</button>
          </div>
          {coldOpen && (
            <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
              {(coldLeads || []).map(l => (
                <div key={l.id} onClick={() => viewLead(l.id)} style={{ padding: '9px 11px', background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 9, cursor: 'pointer' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.profile?.company || l.profile?.name || 'Unknown'}</div>
                  <div style={{ fontSize: 11, color: C.text3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.profile?.email}</div>
                </div>
              ))}
              {coldLeads && !coldLeads.length && <div style={{ color: C.text3, fontSize: 12.5 }}>None to show.</div>}
            </div>
          )}
        </div>
      </div>

      {/* ── Conversation drawer ── */}
      {selectedLead && (
        <>
          <div style={s.overlay} onClick={() => setSelectedLead(null)} />
          <div style={s.drawer}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15, color: '#fff', background: 'linear-gradient(135deg, var(--c-primary), var(--c-primary-dim))' }}>{initials(profile?.company || profile?.name)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <h2 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 17, fontWeight: 700, color: C.text, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{profile?.company || profile?.name || 'Lead'}</h2>
                  {lead && <span style={s.badge(cur.color, `${cur.color}1f`)}>{cur.label}</span>}
                </div>
                <p style={{ color: C.text2, fontSize: 12.5, margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{[profile?.title, profile?.company].filter(Boolean).join(' · ') || profile?.email}</p>
              </div>
              {lead && (
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setStageMenuOpen(o => !o)} style={{ ...s.chip(true), borderColor: cur.color, color: cur.color, background: `${cur.color}1f` }}>Move <ChevronDown size={13} /></button>
                  {stageMenuOpen && (
                    <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 10, background: C.bg1, border: '1px solid var(--c-border-bright)', borderRadius: 12, padding: 6, minWidth: 160, boxShadow: 'var(--shadow-elevated)' }}>
                      {STAGES.map(st => (
                        <button key={st.key} disabled={lead?.stage === st.key} onClick={() => { setStageMenuOpen(false); moveToStage(lead.id, st.key, profile?.company || profile?.name); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none', background: lead?.stage === st.key ? `${st.color}1f` : 'transparent', color: lead?.stage === st.key ? st.color : C.text2, fontSize: 12.5, fontWeight: 600, cursor: lead?.stage === st.key ? 'default' : 'pointer', fontFamily: 'Inter,sans-serif', textAlign: 'left' }}>
                          <span style={{ width: 7, height: 7, borderRadius: 99, background: st.color }} />{st.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <button onClick={() => setSelectedLead(null)} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, width: 32, height: 32, cursor: 'pointer', color: C.text2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><X size={15} /></button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: '11px 20px', borderBottom: `1px solid ${C.border}`, background: C.bg2 }}>
              {profile?.email && <span style={{ fontSize: 12.5, color: C.text2, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Mail size={13} color={C.text3} />{profile.email}</span>}
              {profile?.phone && <span style={{ fontSize: 12.5, color: C.text2, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Phone size={13} color={C.text3} />{profile.phone}</span>}
              {profile?.location && <span style={{ fontSize: 12.5, color: C.text2, display: 'inline-flex', alignItems: 'center', gap: 6 }}><MapPin size={13} color={C.text3} />{profile.location}</span>}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 13, background: C.bg }}>
              {isLoadingLead && <div style={{ textAlign: 'center', color: C.text3, fontSize: 13, padding: 26 }}>Loading conversation…</div>}
              {!isLoadingLead && !thread.length && (
                <div style={{ textAlign: 'center', padding: '44px 22px' }}>
                  <MessageSquare size={34} color={C.text3} style={{ marginBottom: 10 }} />
                  <p style={{ color: C.text2, fontSize: 14, fontWeight: 600, margin: '0 0 4px' }}>No conversation yet</p>
                  <p style={{ color: C.text3, fontSize: 12.5, margin: 0 }}>Your outreach and their replies will appear here.</p>
                </div>
              )}
              {!isLoadingLead && thread.map((m, i) => m.dir === 'out' ? (
                <div key={i} style={s.bOut}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}><span style={{ fontSize: 11, fontWeight: 700, color: C.primary }}>You · {m.account || 'WriteNow'}</span><span style={{ fontSize: 10.5, color: C.text3 }}>{fmtDate(m.at)}</span></div>
                  {m.kind === 'campaign' ? <div style={{ fontSize: 12.5, color: C.text }}>✉️ {m.subject || 'Outreach email sent'}</div> : <div style={{ fontSize: 13, color: C.text, whiteSpace: 'pre-wrap', lineHeight: 1.6, maxHeight: 260, overflowY: 'auto' }}>{(m.body || '').trim().slice(0, 4000)}</div>}
                </div>
              ) : (
                <div key={i} style={s.bIn}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{m.reply.from_name || m.reply.from_email}</span>
                    <span style={{ fontSize: 10.5, color: C.text3 }}>{fmtDate(m.at)}</span>
                    {m.reply.intent && INTENT_META[m.reply.intent] && <span style={s.badge(INTENT_META[m.reply.intent].color, INTENT_META[m.reply.intent].bg)}>{INTENT_META[m.reply.intent].label}</span>}
                  </div>
                  {m.reply.subject && <div style={{ fontSize: 12, fontWeight: 600, color: C.text2, marginBottom: 5 }}>{m.reply.subject}</div>}
                  <div style={{ fontSize: 13, color: C.text, whiteSpace: 'pre-wrap', lineHeight: 1.6, maxHeight: 300, overflowY: 'auto' }}>{(m.reply.body || m.reply.ai_summary || '').trim().slice(0, 4000)}</div>
                </div>
              ))}
              <div ref={threadEndRef} />
            </div>
            <div style={{ borderTop: `1px solid ${C.border}`, padding: '13px 20px 16px', background: C.bg1 }}>
              {lastInbound ? (
                <>
                  {suggested.length > 0 && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 9 }}>{suggested.map((sg, i) => <button key={i} className="dc-sug" onClick={() => setReplyText(sg.body || '')} style={s.chip(false)}><Sparkles size={12} /> {sg.type ? sg.type[0].toUpperCase() + sg.type.slice(1) : `Option ${i + 1}`}</button>)}</div>}
                  <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder={`Reply to ${lastInbound.from_name || lastInbound.from_email}…`} style={s.textarea} />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 9 }}><button onClick={sendReply} disabled={isSendingReply || !replyText.trim()} style={{ ...s.btnP, opacity: isSendingReply || !replyText.trim() ? 0.55 : 1 }}>{isSendingReply ? 'Sending…' : <><Send size={14} /> Send reply</>}</button></div>
                </>
              ) : <p style={{ fontSize: 12.5, color: C.text3, margin: 0, textAlign: 'center' }}>You can reply here once they respond.</p>}
            </div>
          </div>
        </>
      )}

      {/* Won modal */}
      {wonModal && (
        <div style={{ ...s.overlay, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 130 }} onClick={e => e.target === e.currentTarget && setWonModal(null)}>
          <div style={{ background: C.bg1, border: '1px solid var(--c-border-bright)', borderRadius: 22, padding: 38, maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: 'var(--shadow-glow)' }}>
            <div style={{ fontSize: 50, marginBottom: 10 }}>🏆</div>
            <h2 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 8 }}>Deal Won!</h2>
            <p style={{ color: C.text2, fontSize: 14, marginBottom: 22 }}>Sale amount for <strong style={{ color: C.text }}>{wonModal.name}</strong></p>
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontWeight: 700, color: C.primary, fontSize: 18 }}>R</span>
              <input type="number" value={dealAmount} min="0" autoFocus placeholder="0.00" onChange={e => setDealAmount(e.target.value)} onKeyDown={e => e.key === 'Enter' && confirmWon()} style={{ width: '100%', background: C.surface, border: '1px solid var(--c-border-bright)', borderRadius: 12, color: C.text, padding: '14px 16px 14px 44px', fontSize: 20, fontWeight: 700, fontFamily: 'Inter,sans-serif', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => { setWonModal(null); setDealAmount(''); }} style={{ ...s.btn2, flex: 1, justifyContent: 'center' }}>Cancel</button>
              <button onClick={confirmWon} disabled={isSavingDeal || !dealAmount} style={{ ...s.btnP, flex: 1, opacity: isSavingDeal || !dealAmount ? 0.6 : 1 }}>{isSavingDeal ? '…' : <><Trophy size={14} /> Confirm</>}</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ position: 'fixed', bottom: 24, right: 24, display: 'flex', flexDirection: 'column', gap: 10, zIndex: 200 }}>
        {toasts.map(t2 => <div key={t2.id} style={{ padding: '12px 18px', borderRadius: 12, fontSize: 13, fontWeight: 500, backdropFilter: 'blur(16px)', boxShadow: 'var(--shadow-elevated)', background: t2.type === 'error' ? 'rgba(239,68,68,0.08)' : 'rgba(0,179,134,0.08)', border: `1px solid ${t2.type === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(0,179,134,0.15)'}`, color: t2.type === 'error' ? 'var(--c-red)' : 'var(--c-primary)' }}>{t2.msg}</div>)}
      </div>
    </div>
  );
};

export default LeadPipeline;
